import React, { useState, useEffect, useRef } from 'react';
import { clientsAPI } from '../../../api';
import { resolveUrl, overlayStyle, modalStyle, inputStyle, labelStyle } from '../utils';

const chip = (active) => ({
  padding: '4px 12px', fontSize: 11, letterSpacing: '0.06em', cursor: 'pointer',
  border: `1px solid ${active ? 'var(--kahla-charcoal)' : 'var(--kahla-border)'}`,
  borderRadius: 999, background: active ? 'var(--kahla-charcoal)' : 'transparent',
  color: active ? '#faf8f5' : 'var(--kahla-text-light)',
});

export const HistoryEntryEditor = ({ clientId, entry, boards, documents, t, onSaved, onClose, showToast }) => {
  const isEdit = !!entry;
  const [form, setForm] = useState({
    date: entry?.date || new Date().toISOString().slice(0, 10),
    title: entry?.title || '',
    note: entry?.note || '',
    board_id: entry?.board_id || '',
    document_ids: entry?.document_ids || [],
  });
  const [tags, setTags] = useState(entry?.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [photos, setPhotos] = useState(entry?.photos || []);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    clientsAPI.tagSuggestions().then(({ data }) => setSuggestions(data.tags || [])).catch(() => {});
  }, []);

  const addTag = (raw) => {
    const val = (raw || '').trim();
    if (!val) return;
    if (!tags.some((x) => x.toLowerCase() === val.toLowerCase())) setTags([...tags, val]);
    setTagInput('');
  };

  const matching = tagInput.trim()
    ? suggestions.filter((s) => s.toLowerCase().includes(tagInput.trim().toLowerCase()) && !tags.some((x) => x.toLowerCase() === s.toLowerCase())).slice(0, 6)
    : [];

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (isEdit) {
      for (const f of list) {
        try {
          const { data } = await clientsAPI.history.addPhoto(clientId, entry.id, f);
          setPhotos((prev) => [...prev, data]);
        } catch (e) {
          showToast(e.response?.data?.detail || (t.cs_upload_failed || 'Upload failed'));
        }
      }
    } else {
      setPendingFiles((prev) => [...prev, ...list]);
    }
  };

  const removePhoto = async (photoId) => {
    try {
      await clientsAPI.history.removePhoto(clientId, entry.id, photoId);
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
    } catch (e) { /* noop */ }
  };

  const save = async () => {
    if (!form.title.trim() || saving) return;
    setSaving(true);
    const payload = {
      date: form.date, title: form.title, note: form.note, tags,
      board_id: form.board_id || null,
      document_ids: form.document_ids,
    };
    try {
      if (isEdit) {
        await clientsAPI.history.update(clientId, entry.id, payload);
      } else {
        const { data } = await clientsAPI.history.create(clientId, payload);
        for (const f of pendingFiles) {
          try { await clientsAPI.history.addPhoto(clientId, data.id, f); } catch (e) { /* skip */ }
        }
      }
      showToast(t.cs_saved || 'Saved');
      onSaved();
    } catch (e) {
      showToast(e.response?.data?.detail || (t.save_failed || 'Could not save'));
    } finally { setSaving(false); }
  };

  const clientBoards = boards.filter((b) => (b.board_type || 'client') === 'client');

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()} data-testid="history-entry-editor">
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, fontWeight: 400, margin: '0 0 24px', color: 'var(--kahla-charcoal)' }}>
          {isEdit ? (t.cs_edit_entry || 'Edit entry') : (t.cs_add_entry_title || 'New history entry')}
        </h2>
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>{t.cs_entry_date || 'Date'}</label>
              <input data-testid="entry-date-input" type="date" style={inputStyle} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>{t.cs_entry_title || 'Title'} *</label>
              <input data-testid="entry-title-input" style={inputStyle} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t.cs_entry_title_hint || 'e.g. First consultation, Lash refill, Brand workshop…'} autoFocus={!isEdit} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>{t.cs_entry_note || 'Note'}</label>
            <textarea data-testid="entry-note-input" rows={5} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>

          <div>
            <label style={labelStyle}>{t.cs_entry_tags || 'Tags (optional)'}</label>
            {tags.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {tags.map((tag) => (
                  <button key={tag} type="button" style={chip(true)} onClick={() => setTags(tags.filter((x) => x !== tag))} title={t.cs_remove || 'Remove'}>
                    {tag} ×
                  </button>
                ))}
              </div>
            )}
            <input
              data-testid="entry-tags-input"
              style={inputStyle}
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(tagInput); }
              }}
              onBlur={() => addTag(tagInput)}
              placeholder={t.cs_entry_tags_hint || 'Type and press Enter'}
            />
            {matching.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }} data-testid="tag-suggestions">
                {matching.map((s) => (
                  <button key={s} type="button" data-testid={`tag-suggestion-${s}`} style={chip(false)} onClick={() => addTag(s)}>{s}</button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label style={labelStyle}>{t.cs_entry_photos || 'Photos (optional)'}</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {photos.map((p) => (
                <div key={p.id} style={{ position: 'relative' }}>
                  <img src={resolveUrl(p.url)} alt="" style={{ width: 64, height: 64, objectFit: 'cover' }} />
                  <button
                    type="button" data-testid={`entry-photo-remove-${p.id}`}
                    onClick={() => removePhoto(p.id)}
                    style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'var(--kahla-charcoal)', color: '#fff', fontSize: 10, cursor: 'pointer', lineHeight: 1 }}
                  >×</button>
                </div>
              ))}
              {pendingFiles.map((f, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img src={URL.createObjectURL(f)} alt="" style={{ width: 64, height: 64, objectFit: 'cover', opacity: 0.8 }} />
                  <button
                    type="button"
                    onClick={() => setPendingFiles(pendingFiles.filter((_, j) => j !== i))}
                    style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'var(--kahla-charcoal)', color: '#fff', fontSize: 10, cursor: 'pointer', lineHeight: 1 }}
                  >×</button>
                </div>
              ))}
              <button
                type="button" data-testid="entry-add-photo-btn"
                onClick={() => fileRef.current?.click()}
                style={{ width: 64, height: 64, border: '1px dashed var(--kahla-border)', background: 'transparent', cursor: 'pointer', fontSize: 20, color: 'var(--kahla-text-light)' }}
              >+</button>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} data-testid="entry-photo-input" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>{t.cs_entry_link_board || 'Linked board (optional)'}</label>
              <select data-testid="entry-board-select" style={inputStyle} value={form.board_id} onChange={(e) => setForm({ ...form, board_id: e.target.value })}>
                <option value="">{t.cs_none || 'None'}</option>
                {clientBoards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            {documents.length > 0 && (
              <div>
                <label style={labelStyle}>{t.cs_entry_link_documents || 'Linked documents (optional)'}</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {documents.map((d) => {
                    const on = form.document_ids.includes(d.id);
                    return (
                      <button
                        key={d.id} type="button" style={chip(on)}
                        onClick={() => setForm({
                          ...form,
                          document_ids: on ? form.document_ids.filter((x) => x !== d.id) : [...form.document_ids, d.id],
                        })}
                      >{d.title}</button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 28, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-ghost" onClick={onClose}>{t.cs_cancel || 'Cancel'}</button>
          <button type="button" className="btn-primary" data-testid="entry-save-btn" onClick={save} disabled={!form.title.trim() || saving}>
            {saving ? '…' : (t.cs_save || 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HistoryEntryEditor;
